import MapPropertyMarkersView from './map/MapPropertyMarkersView';
import MapPropertyInfoWindowView from './map/MapPropertyInfoWindowView';
import MapLoader from './map/MapLoader';
import { groupPropertiesByLocation, groupPropertiesByNeighborhood, DEFAULT_LOCATION } from './map/helpers';
import PropertiesView from './properties/PropertiesView';
import desktopViewResizer from './map/desktopViewResizer';
import desktopOptionsFn from './map/map.options.desktop';
import { getElement } from '../../common/asyncElementGetter';

const appDesktop = async({ document, properties: _properties = [] }) => {
  const mapElement = document.querySelector('#map');
  const propertiesElement = document.querySelector('#properties');
  const mapWrapperElement = document.querySelector('.map-container');
  const propertyCardElements = document.querySelectorAll('.property_card');
  const properties = _properties.filter(p => p.latitude && p.longitude)
    .map(p => ({ ...p, location: { lat: p.latitude, lng: p.longitude } }));
  const propertyGroups = groupPropertiesByLocation(properties);
  const propertiesByNeighborhod = groupPropertiesByNeighborhood(properties);
  const mapBackButton = await getElement('.map-back-button', false, true, 500, 30);
  const mapButtonElement = await getElement('.show-map-button', false, true, 500, 30);
  const listButtonElement = await getElement('.show-list-button', false, true, 500, 30);

  const desktopViewResizerDestructor = desktopViewResizer({
    observableSource: propertiesElement,
    resizableDestination: mapWrapperElement
  });

  let destructor = null;

  const mapLoader = new MapLoader({ element: mapElement, optionsFn: desktopOptionsFn });
  mapLoader.load();

  const init = (e) => {
    const { element, map } = e.detail;
    const url = window.location.href;
    const isCasatb = url.includes('casatb');

    let mapMarkersView = new MapPropertyMarkersView({ element, map });
    let mapInfoWindowView = new MapPropertyInfoWindowView({ element, map });
    let propertiesView = new PropertiesView({
      element: propertiesElement,
      propertyCardElements: [...propertyCardElements],
      properties
    });

    window.addEventListener('property_cards:update', () => propertiesView.updatePropertyElements());

    // Event handlers
    const handleMapViewMarkerMouseOver = (e) => {
      e.detail.propertyGroup.properties.forEach((property) => {
        propertiesView.highlightProperty(property.id);
      });
    };

    const handleMapViewMarkerMouseOut = (e) => {
      e.detail.propertyGroup.properties.forEach((property) => {
        propertiesView.unHighlightProperty(property.id);
      });
    };

    const handleMapViewMarkerClick = (e) => {
      map.getZoom() < 16
        ? handleMapViewNeighbourhoodMarkerClick(e)
        : handleMapViewApartmentMarkerClick(e);
    };

    const handleMapViewApartmentMarkerClick = (e) => {
      mapInfoWindowView.openInfoWindow(e.detail);
    };

    const handleMapViewNeighbourhoodMarkerClick = (e) => {
      propertiesView.unHighlightAllProperties();
      map.setZoom(16);
      const neighborhoodProperties = propertiesByNeighborhod.find(p => p.neighbourhood === e.detail.propertyGroup.neighbourhood).properties;

      const neighLength = neighborhoodProperties.length;
      const currentNeighbourhood = neighborhoodProperties[0]?.neighbourhood;

      neighLength > 1
        ? map.setCenter({
          lat: currentNeighbourhood.tabas_latitude,
          lng: currentNeighbourhood.tabas_longitude
        })
        : map.setCenter(e.detail.propertyGroup.location);
      mapMarkersView.createMarkers(propertyGroups, 'homesMarker');
    };

    const handlePropertiesViewMouseOver = (e) => {
      mapMarkersView.highlightMarker(e.detail.property.id);
    };

    const handlePropertiesViewMouseOut = (e) => {
      mapMarkersView.unHighlightMarker(e.detail.property.id);
    };

    const handleMapZoom = (zoom) => {
      zoom < 16
        ? mapMarkersView.createMarkers(propertiesByNeighborhod, 'neighbourhoodMarker')
        : mapMarkersView.createMarkers(propertyGroups, 'homesMarker');
    };

    const handleMapButtonClick = (e) => {
      // TODO: encapsulate this
      mapWrapperElement.classList.remove('w-pc-md-50', 'w-pc-xl-40', 'd-none');
      mapWrapperElement.classList.add('d-block');
      propertiesElement.classList.add('d-none');
      mapButtonElement.classList.add('d-none');
      listButtonElement.classList.add('d-sm-inline-flex');
      tryFitBounds();
    };

    const handleListButtonClick = (e) => {
      // TODO: encapsulate this
      mapWrapperElement.classList.add('w-pc-md-50', 'w-pc-xl-40', 'd-none');
      mapWrapperElement.classList.remove('d-block');
      propertiesElement.classList.remove('d-none');
      mapButtonElement.classList.remove('d-none');
      listButtonElement.classList.remove('d-sm-inline-flex');
      tryFitBounds();
    };

    const tryFitBounds = () => {
      if (properties.length) {
        map.fitBounds(mapLoader.getBounds());
      } else {
        map.setCenter(DEFAULT_LOCATION);
      }
    };

    // place markers
    mapMarkersView.createMarkers(propertiesByNeighborhod, 'neighbourhoodMarker');

    if (isCasatb) {
      if (properties.some(p => url.includes(p.neighbourhood.slug))) {
        map.setCenter({ lat: properties[0].location.lat, lng: properties[0].location.lng });
        map.panTo({ lat: properties[0].location.lat, lng: properties[0].location.lng });
      } else {
        map.setCenter(DEFAULT_LOCATION);
      }
    } else {
      tryFitBounds();
    }

    if (propertiesByNeighborhod.length === 1) {
      const currentProperties = propertiesByNeighborhod[0].properties;
      if (currentProperties.length) {
        const currentNeighbourhood = currentProperties[0].neighbourhood;
        map.panTo({ lat: currentNeighbourhood.tabas_latitude, lng: currentNeighbourhood.tabas_longitude });
      }
    }

    // mouse events on property cards of property list will highlight markers in map
    mapMarkersView.on('marker:mouseover', handleMapViewMarkerMouseOver);
    mapMarkersView.on('marker:mouseout', handleMapViewMarkerMouseOut);
    mapMarkersView.on('marker:click', handleMapViewMarkerClick);

    // mouse events on markers in map will highlight property cards in property list
    propertiesView.on('property:mouseover', handlePropertiesViewMouseOver);
    propertiesView.on('property:mouseout', handlePropertiesViewMouseOut);

    // mouse events on buttons list and map view
    mapButtonElement.addEventListener('click', handleMapButtonClick);
    listButtonElement.addEventListener('click', handleListButtonClick);
    mapBackButton.addEventListener('click', handleListButtonClick);

    // Map Zoom Listener
    map.addListener('zoom_changed', () => {
      const currentZoom = map.getZoom();
      handleMapZoom(currentZoom);
    });

    destructor = () => {
      desktopViewResizerDestructor();

      mapLoader.off('ready', init);
      mapMarkersView.off('marker:mouseover', handleMapViewMarkerMouseOver);
      mapMarkersView.off('marker:mouseout', handleMapViewMarkerMouseOut);
      mapMarkersView.off('marker:click', handleMapViewMarkerClick);
      mapMarkersView.destroy();
      mapMarkersView = null;

      mapInfoWindowView.destroy();
      mapInfoWindowView = null;

      propertiesView.off('property:mouseover', handlePropertiesViewMouseOver);
      propertiesView.off('property:mouseout', handlePropertiesViewMouseOut);
      propertiesView.destroy();
      propertiesView = null;

      mapButtonElement.removeEventListener('click', handleMapButtonClick);
      listButtonElement.removeEventListener('click', handleListButtonClick);
    };
  };

  mapLoader.on('ready', init);

  return {
    destroy: () => {
      destructor && destructor();
    }
  };
};

export default appDesktop;
